import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faExternalLinkAlt, faTimes } from "@fortawesome/free-solid-svg-icons";

const DetailedPaymentReportViewModal = ({
  isOpen,
  onClose,
  row,
  columns,
  toDisplayValue,
  resolvedSqlQuery,
  onViewFullPage,
}) => {
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  const debugEntries = useMemo(() => {
    if (!resolvedSqlQuery || typeof resolvedSqlQuery !== "object") {
      return [];
    }

    return Object.entries(resolvedSqlQuery).filter(([, value]) => Boolean(value));
  }, [resolvedSqlQuery]);

  if (!isOpen || !row) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
      <div className="relative max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-[28px] border border-cyan-100 bg-white shadow-2xl">
        <div className="bg-gradient-to-r from-cyan-600 via-sky-600 to-blue-700 px-6 py-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100">
                Provider Payment
              </p>
              <h2 className="mt-1 text-2xl font-bold">Detailed Payment Report Viewer</h2>
              <p className="mt-2 text-sm text-cyan-50">
                Review the full row details for transaction {row?.trans_no || "N/A"}.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {onViewFullPage && (
                <button
                  type="button"
                  onClick={() => onViewFullPage(row)}
                  className="relative inline-flex items-center gap-2 rounded-lg border border-white/25 bg-white px-3 py-2 text-xs font-bold text-rose-700 shadow-sm transition hover:bg-rose-50"
                >
                  <span className="absolute -right-2 -top-2 rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none text-white shadow-sm">
                    New
                  </span>
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                  View in full page
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white transition hover:bg-white/20"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        </div>

        <div className="max-h-[calc(90vh-104px)] overflow-y-auto bg-gradient-to-b from-slate-50 to-white p-6">
          <div className="mb-5 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-cyan-100 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Transaction No.</div>
              <div className="mt-2 text-lg font-bold text-gray-900">{toDisplayValue(row, "trans_no") || "N/A"}</div>
            </div>
            <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Payment Ref. No.</div>
              <div className="mt-2 text-lg font-bold text-gray-900">{toDisplayValue(row, "payment_ref_no") || "N/A"}</div>
            </div>
            <div className="rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Net Payable</div>
              <div className="mt-2 text-lg font-bold text-emerald-700">{toDisplayValue(row, "net_payable") || "N/A"}</div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {columns.map((column) => (
              <div
                key={column.key}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-cyan-200 hover:shadow-md"
              >
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                  {column.label}
                </div>
                <div className="mt-2 break-words text-sm font-medium text-gray-900">
                  {toDisplayValue(row, column.key) || "N/A"}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-sm">
            <button
              type="button"
              onClick={() => setShowDebugInfo((prev) => !prev)}
              className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
            >
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                  Debug Tools
                </div>
                <div className="mt-1 text-sm font-semibold text-gray-900">
                  Resolved SQL Query Viewer
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  Collapsible section for debugging. Hidden by default for normal users.
                </div>
              </div>
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600">
                <FontAwesomeIcon icon={showDebugInfo ? faChevronDown : faChevronRight} />
              </span>
            </button>

            {showDebugInfo && (
              <div className="border-t border-slate-200 px-4 py-4">
                {debugEntries.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                    No resolved SQL debug information is available for this view yet.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {debugEntries.map(([key, value]) => (
                      <div key={key} className="rounded-2xl border border-slate-200 bg-slate-950 p-4 text-slate-100 shadow-inner">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                          {key}
                        </div>
                        <pre className="overflow-x-auto whitespace-pre-wrap break-words text-xs leading-6 text-slate-200">
                          {String(value)}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedPaymentReportViewModal;
